import { useEffect, useState } from "react";

import { useFetchSftpSettings } from "src/queries/hooks/admin";
import { useFetchAccountSftpSettings } from "src/queries/hooks/accountSettings";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import useAppStore from "src/stores/appStore";
import { hasPermissionSelector } from "src/stores/selectors/appSelectors";
import { DIALOG_TYPES } from "./AccountEditorConsts";
import { isBlacklistedDomain, validateEmailAddress, validateIpAddress, validatePiiRetentionDays } from "./AccountEditor.utils";
import useAccountEditorDialog from "./useAccountEditorDialog";

const useAccountEditorController = ({
  accountData,
  type,
  error,
  errorText,
  onConfirm,
  onResetError,
  isAccountContext
}) => {
  const hasPermission = useAppStore(hasPermissionSelector);
  const hasWritePermission = !isAccountContext || hasPermission(USER_PERMISSIONS.ACCOUNT_SETTINGS.WRITE);
  // Changing the retention policy needs a dedicated permission on top of the account-settings one.
  // Unlike hasWritePermission, this is deliberately NOT short-circuited on the admin route: reaching
  // the admin page does not by itself grant control over a data-deletion policy, so the permission is
  // enforced in both contexts. The field stays visible without it, only disabled, so the current
  // policy is always readable.
  const hasPiiRetentionWritePermission = hasPermission(USER_PERMISSIONS.PII_RETENTION.WRITE);
  const [newAccountData, setNewAccountData] = useState(accountData);

  useEffect(() => {
    setNewAccountData((prev) => {
      if (!prev) return accountData;
      return {
        ...accountData,
        sftpUrl: prev.sftpUrl,
        proactiveSftpUrl: prev.proactiveSftpUrl,
        sftpIpList: prev.sftpIpList,
        publicKey: prev.publicKey
      };
    });
  }, [accountData]);

  const { dialogContent } = useAccountEditorDialog({
    error,
    errorText,
    type,
    accountData: newAccountData
  });

  const accountId = accountData?.accountId?.value;

  const { data: adminSftpSettings, isLoading: isLoadingAdminSftp } = useFetchSftpSettings({
    accountId,
    enabled: !isAccountContext && type === "edit" && !!accountId
  });

  const { data: accountSftpSettings, isLoading: isLoadingAccountSftp } = useFetchAccountSftpSettings({
    accountId,
    enabled: !!isAccountContext && type === "edit" && !!accountId
  });

  const sftpSettings = isAccountContext ? accountSftpSettings : adminSftpSettings;
  const isLoadingSftpSettings = isAccountContext ? isLoadingAccountSftp : isLoadingAdminSftp;

  useEffect(() => {
    if (sftpSettings) {
      setNewAccountData((prev) => ({
        ...prev,
        sftpUrl: { value: sftpSettings.sftpUrl || "", errorText: "" },
        proactiveSftpUrl: { value: sftpSettings.proactiveSftpUrl || "", errorText: "" },
        sftpIpList: { value: sftpSettings.ipAddresses || [], errorText: "" },
        publicKey: { value: sftpSettings.publicKey || "", errorText: "" }
      }));
    }
  }, [sftpSettings]);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (error) {
      setIsLoading(false);
      setIsConfirmationDialogOpen(false);
    }
  }, [error]);

  const hasNameError = !!newAccountData?.name.errorText;
  const hasAccountIdError = !!newAccountData?.accountId.errorText;
  const hasSalesforceIdError = !!newAccountData?.salesforceAccountId?.errorText;
  const baseSftpIpList = sftpSettings?.ipAddresses || accountData?.sftpIpList?.value || [];
  const basePublicKey = sftpSettings?.publicKey || accountData?.publicKey?.value || "";
  const noChanges = newAccountData?.name.value === accountData?.name.value
    && newAccountData?.accountId.value === accountData?.accountId.value
    && newAccountData?.salesforceAccountId?.value === accountData?.salesforceAccountId?.value
    && newAccountData?.timezone?.value === accountData?.timezone?.value
    && newAccountData?.accountType?.value === accountData?.accountType?.value
    && JSON.stringify(newAccountData?.sftpIpList?.value) === JSON.stringify(baseSftpIpList)
    && newAccountData?.publicKey?.value === basePublicKey
    && JSON.stringify(newAccountData?.supportedDomains?.value) === JSON.stringify(accountData?.supportedDomains?.value)
    && JSON.stringify(newAccountData?.extraSupportedEmails?.value) === JSON.stringify(accountData?.extraSupportedEmails?.value)
    && newAccountData?.piiRetentionDays?.value === accountData?.piiRetentionDays?.value;

  const sftpIpValues = newAccountData?.sftpIpList?.value || [];
  const invalidIpIndexes = sftpIpValues.reduce((acc, ip, index) => {
    if (ip && !validateIpAddress(ip)) acc.push(index);
    return acc;
  }, []);
  const hasInvalidIps = invalidIpIndexes.length > 0;

  const supportedDomainsValues = newAccountData?.supportedDomains?.value || [];
  const invalidSupportedDomainIndexes = supportedDomainsValues.reduce((acc, domain, index) => {
    if (domain && isBlacklistedDomain(domain)) acc.push(index);
    return acc;
  }, []);
  const hasInvalidSupportedDomains = invalidSupportedDomainIndexes.length > 0;

  const extraSupportedEmailsValues = newAccountData?.extraSupportedEmails?.value || [];
  const invalidExtraSupportedEmailIndexes = extraSupportedEmailsValues.reduce((acc, email, index) => {
    if (email && !validateEmailAddress(email)) acc.push(index);
    return acc;
  }, []);
  const hasInvalidExtraSupportedEmails = invalidExtraSupportedEmailIndexes.length > 0;

  const hasPiiRetentionDaysError = !!validatePiiRetentionDays(newAccountData?.piiRetentionDays?.value);

  const isSaveDisabled = type !== "delete" && (hasNameError || hasAccountIdError || hasSalesforceIdError || hasInvalidIps || hasInvalidSupportedDomains || hasInvalidExtraSupportedEmails || hasPiiRetentionDaysError || noChanges || !hasWritePermission);

  const handleTextFieldChange = ({ target }, validationFn) => {
    const errorText = validationFn ? validationFn(target.value) : "";

    setNewAccountData((prev) => ({
      ...prev,
      [target.name]: { value: target.value, errorText }
    }));
  };

  const handleSaveButtonClicked = () => {
    if (error) return onResetError();

    if (type === DIALOG_TYPES.create || type === DIALOG_TYPES.delete) {
      typeof onConfirm === "function" && onConfirm(newAccountData);
      setIsLoading(true);
    }
    else {
      setIsConfirmationDialogOpen(true);
    }
  };

  const handleSubmit = () => {
    typeof onConfirm === "function" && onConfirm(newAccountData);
    setIsLoading(true);
  };

  const handleConfirmationClose = () => {
    setIsConfirmationDialogOpen(false);
  };

  return {
    newAccountData,
    dialogContent,
    isConfirmationDialogOpen,
    isLoading,
    isLoadingSftpSettings,
    isSaveDisabled,
    isReadOnly: !hasWritePermission,
    isPiiRetentionDaysReadOnly: !hasWritePermission || !hasPiiRetentionWritePermission,
    invalidIpIndexes,
    invalidSupportedDomainIndexes,
    invalidExtraSupportedEmailIndexes,
    handleTextFieldChange,
    handleSaveButtonClicked,
    handleSubmit,
    handleConfirmationClose
  };
};

export default useAccountEditorController;

